// src/pages/BlogList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Calendar, Clock, User, Tag, ChevronLeft, ChevronRight, BookOpen, ArrowRight, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BlogList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [categories, setCategories] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    primaryLight: '#4d7df7',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray300: '#cbd5e1',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  const categoryColors = {
    'Savings Tips': '#10b981',
    'Drug Information': '#2055df',
    'Health & Wellness': '#06b6d4',
    'Insurance': '#8b5cf6',
    'Pharmacy': '#f59e0b',
    'Medicare': '#ef4444',
    'Generic Drugs': '#10b981',
    'Prescription Help': '#ec4899',
    default: '#2055df'
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.title = 'Rx Savings Blog - Tips & Guides | Rx.ChatWithDr';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Expert tips on saving money on prescriptions, understanding generic drugs, navigating insurance, and improving your health. Free resources from Rx.ChatWithDr.');
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'description';
      newMeta.content = 'Expert tips on saving money on prescriptions, understanding generic drugs, navigating insurance, and improving your health. Free resources from Rx.ChatWithDr.';
      document.head.appendChild(newMeta);
    }
  }, []);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', currentPage);
      if (activeCategory) params.set('category', activeCategory);
      if (searchQuery) params.set('search', searchQuery);

      const response = await axios.get(`/api/blog?${params.toString()}`);
      const data = response.data;
      setBlogs(data.posts || data.blogs || []);
      setTotalPages(data.totalPages || 1);
      setTotalPosts(data.total || 0);
      if (data.categories) setCategories(data.categories);
      if (data.popularPosts) setPopularPosts(data.popularPosts);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, activeCategory, searchQuery]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage);
    if (activeCategory) params.set('category', activeCategory);
    if (searchQuery) params.set('search', searchQuery);
    setSearchParams(params, { replace: true });
  }, [currentPage, activeCategory, searchQuery, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category === activeCategory ? '' : category);
    setCurrentPage(1);
  };

  const getCategoryColor = (category) => {
    return categoryColors[category] || categoryColors.default;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // -- Skeleton loader --
  const SkeletonCard = () => (
    <div style={{
      background: colors.white,
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      <div style={{ height: 200, background: `linear-gradient(135deg, ${colors.gray200}, ${colors.gray100})` }}>
        <div style={{
          width: '100%', height: '100%',
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
          animation: 'shimmer 1.5s infinite',
        }} />
      </div>
      <div style={{ padding: 24 }}>
        <div style={{ width: 80, height: 24, borderRadius: 12, background: colors.gray200, marginBottom: 12 }} />
        <div style={{ width: '90%', height: 20, borderRadius: 4, background: colors.gray200, marginBottom: 8 }} />
        <div style={{ width: '70%', height: 20, borderRadius: 4, background: colors.gray200, marginBottom: 16 }} />
        <div style={{ width: '100%', height: 14, borderRadius: 4, background: colors.gray100, marginBottom: 6 }} />
        <div style={{ width: '85%', height: 14, borderRadius: 4, background: colors.gray100, marginBottom: 20 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: 100, height: 14, borderRadius: 4, background: colors.gray100 }} />
          <div style={{ width: 60, height: 14, borderRadius: 4, background: colors.gray100 }} />
        </div>
      </div>
    </div>
  );

  // -- Blog Card --
  const BlogCard = ({ blog, index }) => {
    const [hovered, setHovered] = useState(false);

    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.08 }}
      >
        <Link
          to={`/blog/${blog.slug}`}
          style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div style={{
            background: colors.white,
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: hovered ? '0 12px 32px rgba(32,85,223,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
            transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            border: `1px solid ${hovered ? colors.primary + '30' : colors.gray200}`,
          }}>
            {/* Featured Image - only show if image exists */}
            {blog.featuredImage && (
              <div style={{
                height: 200,
                background: `url(${blog.featuredImage}) center/cover no-repeat`,
                position: 'relative',
              }}>
                {blog.category && (
                  <span style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    background: getCategoryColor(blog.category),
                    color: colors.white,
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                  }}>
                    {blog.category}
                  </span>
                )}
              </div>
            )}

            {/* Content */}
            <div style={{ padding: 24 }}>
              {/* Category badge when no image */}
              {!blog.featuredImage && blog.category && (
                <span style={{
                  display: 'inline-block',
                  background: getCategoryColor(blog.category),
                  color: colors.white,
                  padding: '4px 12px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  marginBottom: 12,
                }}>
                  {blog.category}
                </span>
              )}
              <h3 style={{
                fontSize: 18,
                fontWeight: 700,
                color: colors.gray900,
                marginBottom: 8,
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {blog.title}
              </h3>

              <p style={{
                fontSize: 14,
                color: colors.gray500,
                lineHeight: 1.6,
                marginBottom: 16,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {blog.excerpt || blog.metaDescription || ''}
              </p>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                  {blog.tags.slice(0, 3).map((tag, i) => (
                    <span key={i} style={{
                      fontSize: 11,
                      color: colors.gray500,
                      background: colors.gray100,
                      padding: '2px 8px',
                      borderRadius: 4,
                    }}>
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Meta */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: 16,
                borderTop: `1px solid ${colors.gray100}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <User size={14} color={colors.gray400} />
                  <span style={{ fontSize: 13, color: colors.gray500 }}>
                    {blog.author || 'Rx.ChatWithDr Editorial'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={13} color={colors.gray400} />
                    <span style={{ fontSize: 12, color: colors.gray400 }}>
                      {formatDate(blog.publishedAt || blog.createdAt)}
                    </span>
                  </div>
                  {blog.readingTime && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={13} color={colors.gray400} />
                      <span style={{ fontSize: 12, color: colors.gray400 }}>
                        {blog.readingTime} min
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  };

  // -- Pagination --
  const Pagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = isMobile ? 3 : 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

    for (let i = start; i <= end; i++) pages.push(i);

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 48,
      }}>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40, borderRadius: 10,
            border: `1px solid ${colors.gray200}`,
            background: colors.white,
            cursor: currentPage === 1 ? 'default' : 'pointer',
            opacity: currentPage === 1 ? 0.4 : 1,
            color: colors.gray600,
          }}
        >
          <ChevronLeft size={18} />
        </button>
        {start > 1 && (
          <>
            <button onClick={() => setCurrentPage(1)} style={pageButtonStyle(1 === currentPage)}>1</button>
            {start > 2 && <span style={{ color: colors.gray400 }}>...</span>}
          </>
        )}
        {pages.map(p => (
          <button key={p} onClick={() => setCurrentPage(p)} style={pageButtonStyle(p === currentPage)}>
            {p}
          </button>
        ))}
        {end < totalPages && (
          <>
            {end < totalPages - 1 && <span style={{ color: colors.gray400 }}>...</span>}
            <button onClick={() => setCurrentPage(totalPages)} style={pageButtonStyle(totalPages === currentPage)}>
              {totalPages}
            </button>
          </>
        )}
        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40, borderRadius: 10,
            border: `1px solid ${colors.gray200}`,
            background: colors.white,
            cursor: currentPage === totalPages ? 'default' : 'pointer',
            opacity: currentPage === totalPages ? 0.4 : 1,
            color: colors.gray600,
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    );
  };

  const pageButtonStyle = (active) => ({
    width: 40,
    height: 40,
    borderRadius: 10,
    border: active ? 'none' : `1px solid ${colors.gray200}`,
    background: active ? colors.primary : colors.white,
    color: active ? colors.white : colors.gray600,
    fontWeight: active ? 700 : 500,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  // -- Sidebar --
  const Sidebar = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Popular Categories */}
      {categories.length > 0 && (
        <div style={{
          background: colors.white,
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          border: `1px solid ${colors.gray200}`,
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.gray900, marginBottom: 16 }}>
            Popular Categories
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {categories.map((cat, i) => (
              <button
                key={i}
                onClick={() => handleCategoryClick(typeof cat === 'string' ? cat : cat.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: 'none',
                  background: activeCategory === (typeof cat === 'string' ? cat : cat.name)
                    ? colors.primary + '10'
                    : colors.gray100,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                <span style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: activeCategory === (typeof cat === 'string' ? cat : cat.name)
                    ? colors.primary
                    : colors.gray700,
                }}>
                  {typeof cat === 'string' ? cat : cat.name}
                </span>
                {typeof cat === 'object' && cat.count != null && (
                  <span style={{
                    fontSize: 12,
                    color: colors.gray400,
                    background: colors.white,
                    padding: '2px 8px',
                    borderRadius: 8,
                  }}>
                    {cat.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent Posts */}
      {popularPosts.length > 0 && (
        <div style={{
          background: colors.white,
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          border: `1px solid ${colors.gray200}`,
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.gray900, marginBottom: 16 }}>
            Recent Posts
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {popularPosts.slice(0, 5).map((post, i) => (
              <Link
                key={i}
                to={`/blog/${post.slug}`}
                style={{
                  textDecoration: 'none',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: 10,
                  flexShrink: 0,
                  background: post.featuredImage
                    ? `url(${post.featuredImage}) center/cover no-repeat`
                    : `linear-gradient(135deg, ${colors.primary}20, ${colors.accent}20)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {!post.featuredImage && <BookOpen size={20} color={colors.primary} style={{ opacity: 0.4 }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.gray800,
                    lineHeight: 1.4,
                    marginBottom: 4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {post.title}
                  </h4>
                  <span style={{ fontSize: 11, color: colors.gray400 }}>
                    {formatDate(post.publishedAt || post.createdAt)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
        borderRadius: 16,
        padding: 24,
        color: colors.white,
        textAlign: 'center',
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
          Save on Prescriptions
        </h3>
        <p style={{ fontSize: 14, opacity: 0.9, marginBottom: 16, lineHeight: 1.6 }}>
          Get your free Rx discount card and save up to 80% at the pharmacy.
        </p>
        <Link to="/card" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: colors.white,
          color: colors.primary,
          padding: '10px 20px',
          borderRadius: 10,
          fontWeight: 600,
          fontSize: 14,
          textDecoration: 'none',
          transition: 'all 0.2s ease',
        }}>
          Get Free Card <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: colors.bg }}>
      {/* Shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* Hero Section */}
      <section style={{
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 60%, #0f2a7a 100%)`,
        padding: isMobile ? '48px 20px 40px' : '64px 20px 56px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute',
          top: -60,
          right: -60,
          width: 250,
          height: 250,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: -40,
          left: -40,
          width: 180,
          height: 180,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />

        <div style={{ maxWidth: 700, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(255,255,255,0.12)',
              padding: '6px 16px',
              borderRadius: 20,
              marginBottom: 20,
            }}>
              <BookOpen size={16} color={colors.white} />
              <span style={{ color: colors.white, fontSize: 13, fontWeight: 500 }}>Rx.ChatWithDr Blog</span>
            </div>
            <h1 style={{
              fontSize: isMobile ? 32 : 44,
              fontWeight: 800,
              color: colors.white,
              marginBottom: 16,
              lineHeight: 1.2,
            }}>
              Rx Savings Blog
            </h1>
            <p style={{
              fontSize: isMobile ? 16 : 18,
              color: 'rgba(255,255,255,0.8)',
              lineHeight: 1.6,
              maxWidth: 560,
              margin: '0 auto',
            }}>
              Expert tips on prescription savings, understanding your medications, and making healthcare more affordable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filters */}
      <section style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '24px 16px 0' : '32px 20px 0',
      }}>
        {/* Search Bar */}
        <form onSubmit={handleSearch} style={{
          display: 'flex',
          gap: 12,
          marginBottom: 20,
          maxWidth: 600,
        }}>
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            background: colors.white,
            borderRadius: 12,
            border: `1px solid ${colors.gray200}`,
            padding: '0 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <Search size={18} color={colors.gray400} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                padding: '12px',
                fontSize: 15,
                color: colors.gray800,
                background: 'transparent',
              }}
            />
          </div>
          <button type="submit" style={{
            background: colors.primary,
            color: colors.white,
            border: 'none',
            borderRadius: 12,
            padding: '0 24px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            transition: 'background 0.2s ease',
          }}>
            Search
          </button>
        </form>

        {/* Category Filters */}
        {categories.length > 0 && (
          <div style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            paddingBottom: 8,
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}>
            <button
              onClick={() => handleCategoryClick('')}
              style={{
                padding: '8px 18px',
                borderRadius: 20,
                border: !activeCategory ? 'none' : `1px solid ${colors.gray200}`,
                background: !activeCategory ? colors.primary : colors.white,
                color: !activeCategory ? colors.white : colors.gray600,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
            >
              All Posts
            </button>
            {categories.map((cat, i) => {
              const name = typeof cat === 'string' ? cat : cat.name;
              const isActive = activeCategory === name;
              return (
                <button
                  key={i}
                  onClick={() => handleCategoryClick(name)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 20,
                    border: isActive ? 'none' : `1px solid ${colors.gray200}`,
                    background: isActive ? getCategoryColor(name) : colors.white,
                    color: isActive ? colors.white : colors.gray600,
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                  }}
                >
                  {name}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Main Content */}
      <section style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '24px 16px 48px' : '32px 20px 64px',
        display: 'flex',
        gap: 32,
        alignItems: 'flex-start',
      }}>
        {/* Blog Grid */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Results count */}
          {!loading && (
            <p style={{ fontSize: 14, color: colors.gray500, marginBottom: 20 }}>
              {totalPosts > 0
                ? `Showing ${blogs.length} of ${totalPosts} article${totalPosts !== 1 ? 's' : ''}`
                : ''}
              {activeCategory ? ` in "${activeCategory}"` : ''}
              {searchQuery ? ` matching "${searchQuery}"` : ''}
            </p>
          )}

          {loading ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 24,
            }}>
              {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : blogs.length === 0 ? (
            /* Empty State */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                textAlign: 'center',
                padding: '64px 20px',
                background: colors.white,
                borderRadius: 16,
                border: `1px solid ${colors.gray200}`,
              }}
            >
              <BookOpen size={48} color={colors.gray300} style={{ marginBottom: 16 }} />
              <h3 style={{ fontSize: 20, fontWeight: 700, color: colors.gray800, marginBottom: 8 }}>
                No articles found
              </h3>
              <p style={{ fontSize: 15, color: colors.gray500, marginBottom: 24 }}>
                {searchQuery
                  ? `No results for "${searchQuery}". Try a different search term.`
                  : 'There are no blog posts available yet. Check back soon!'}
              </p>
              {(searchQuery || activeCategory) && (
                <button
                  onClick={() => { setSearchQuery(''); setActiveCategory(''); setCurrentPage(1); }}
                  style={{
                    background: colors.primary,
                    color: colors.white,
                    border: 'none',
                    borderRadius: 10,
                    padding: '10px 24px',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  Clear Filters
                </button>
              )}
            </motion.div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: 24,
              }}>
                {blogs.map((blog, i) => (
                  <BlogCard key={blog._id || blog.slug || i} blog={blog} index={i} />
                ))}
              </div>
              <Pagination />
            </>
          )}
        </div>

        {/* Sidebar - Desktop only */}
        {!isMobile && (
          <div style={{ width: 300, flexShrink: 0 }}>
            <Sidebar />
          </div>
        )}
      </section>

      {/* Medical Disclaimer */}
      <section style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '0 16px 48px' : '0 20px 64px',
      }}>
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 16,
          padding: isMobile ? 20 : 24,
          display: 'flex',
          gap: 16,
          alignItems: isMobile ? 'flex-start' : 'center',
        }}>
          <AlertTriangle size={24} color={colors.warning} style={{ flexShrink: 0, marginTop: isMobile ? 2 : 0 }} />
          <div>
            <h4 style={{ fontSize: 15, fontWeight: 700, color: colors.gray800, marginBottom: 4 }}>
              Medical Disclaimer
            </h4>
            <p style={{ fontSize: 13, color: colors.gray600, lineHeight: 1.6, margin: 0 }}>
              The content on this blog is for informational purposes only and does not constitute medical advice.
              Always consult a qualified healthcare professional before making decisions about your medications or treatment.
              Rx.ChatWithDr is not a substitute for professional medical advice, diagnosis, or treatment.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogList;
